import { RefObject, useEffect, useRef } from 'react';

interface UsePullToRefreshOptions {
  target?: HTMLElement | null;
  targetRef?: RefObject<HTMLElement | null>;
  getTarget?: () => HTMLElement | null;
  onRefresh: () => void | Promise<void>;
  isEnabled?: boolean;
  threshold?: number;
}

export const usePullToRefresh = ({
  target,
  targetRef,
  getTarget,
  onRefresh,
  isEnabled = true,
  threshold = 60
}: UsePullToRefreshOptions) => {
  const startYRef = useRef<number | null>(null);
  const hasTriggeredRef = useRef(false);
  const latestCallbackRef = useRef(onRefresh);

  useEffect(() => {
    latestCallbackRef.current = onRefresh;
  }, [onRefresh]);

  const targetElement = target ?? targetRef?.current ?? null;

  useEffect(() => {
    if (!isEnabled) {
      return;
    }

    const isHtmlElement = (value: unknown): value is HTMLElement =>
      typeof HTMLElement !== 'undefined' && value instanceof HTMLElement;

    const resolveScrollContainer = () => {
      const customTarget = getTarget?.();
      if (customTarget) {
        return customTarget;
      }

      return isHtmlElement(targetElement) ? targetElement : null;
    };

    const getScrollTop = () => {
      const scrollContainer = resolveScrollContainer();

      if (scrollContainer) {
        return scrollContainer.scrollTop;
      }

      if (typeof window !== 'undefined') {
        return (
          window.scrollY ??
          window.pageYOffset ??
          document.documentElement.scrollTop ??
          document.body.scrollTop ??
          0
        );
      }

      return 0;
    };

    const handleTouchStart = (event: Event) => {
      const touchEvent = event as TouchEvent;

      if (getScrollTop() > 0) {
        startYRef.current = null;
        hasTriggeredRef.current = false;
        return;
      }

      startYRef.current = touchEvent.touches?.[0]?.clientY ?? null;
      hasTriggeredRef.current = false;
    };

    const handleTouchMove = (event: Event) => {
      if (startYRef.current === null) {
        return;
      }

      const touchEvent = event as TouchEvent;
      const currentY = touchEvent.touches?.[0]?.clientY ?? 0;
      const distance = currentY - startYRef.current;

      if (distance > threshold && !hasTriggeredRef.current) {
        hasTriggeredRef.current = true;
        void latestCallbackRef.current();
      } else if (distance < 0) {
        startYRef.current = null;
        hasTriggeredRef.current = false;
      }
    };

    const handleTouchEnd = () => {
      startYRef.current = null;
      hasTriggeredRef.current = false;
    };

    const eventTarget: EventTarget =
      targetElement && targetElement instanceof EventTarget ? targetElement : window;

    if (!eventTarget) {
      return;
    }

    eventTarget.addEventListener('touchstart', handleTouchStart, { passive: true });
    eventTarget.addEventListener('touchmove', handleTouchMove, { passive: true });
    eventTarget.addEventListener('touchend', handleTouchEnd, { passive: true });
    eventTarget.addEventListener('touchcancel', handleTouchEnd, { passive: true });

    return () => {
      eventTarget.removeEventListener('touchstart', handleTouchStart);
      eventTarget.removeEventListener('touchmove', handleTouchMove);
      eventTarget.removeEventListener('touchend', handleTouchEnd);
      eventTarget.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [targetElement, getTarget, isEnabled, threshold]);
};
